import { tradePromoOptimizationUrls } from 'config/urls';
import { UserContext } from 'contexts/ConfigContext';
import { TPODataContext } from 'contexts/TPOContext';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    formatNumberWithCommas,
    generateErrorMsg,
    getConfig,
    removePropertyByKeyName,
    sendNotification,
    transformFiltersV2,
} from 'utils/helperMethods';
import _ from 'lodash';
import KPISelector from './AddEventModal/PromoSections/KPISelector';
import { Button } from 'antd';
import { ProjectOutlined } from '@ant-design/icons';
import InfiniteScroller from '../InfiniteScroller';
import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { useFetchToolsLibraryData } from 'hooks/tradePromoOptimization/plans/useFetchToolsLibraryData';
import { getPromoLibraryTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import Footer from '../PricingSimulator/Footer';
import TableErrorBox from '../ErrorBox/TableErrorBox';
import EditToolsMetricsModal, { DataType } from './Modals/EditToolsMetricsModal';
import { queryClient } from 'services/reactQueryService';
import { PromoLibraryKpiConfigType } from 'types/tradePromoOptimization';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { transformFilterObjectWithDimensionsInfo } from 'utils/getPricingSimulatorTableColumns';
import NewGlobalFilter from '../common/NewGlobalFilter/NewGlobalFilter';
import { ToolTabKeys } from 'components/organism/TradePromoOptimization/Tools';
import FilterRibbon from '../PricingSimulator/FilterRibbon/FilterRibbon';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';

const promoLibraryTableId = 'promo-library-table-id';

export const initialEditModalState: { open: boolean; kpiData: DataType } = {
    open: false,
    kpiData: { kpiObject: {} },
};

const PromoLibraryContainer = () => {
    const { appConfig } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const {
        availableFilters,
        allCheckedAttribute,
        setAllCheckedAttribute,
        allSelectedValueForAllAttribute,
        setAllSelectedValueForAllAttribute,
        setApplyFilterObject,
        applyFilterObject,
        resetFilterObject,
        uiDimensionConfigData,
        filterUIDimensionsLoading,
    } = useContext(TPODataContext);

    const [openEditModal, setOpenEditModal] = useState({ ...initialEditModalState });
    const [promoLibraryKpiConfigData, setPromoLibraryKpiConfigData] = useState<null | PromoLibraryKpiConfigType>(null);
    const [promoLibraryTableVariable, setPromoLibraryTableVariable] = useState<any>({
        limit: tpoConfig?.tableLimit,
        //limit: 20,
        offset: 0,
        kpis: [],
        library_group: ToolTabKeys.PromoLibrary,
        filters: {},
    });
    const [isKpiFilterVisible, setIsKpiFilterVisible] = useState(false);
    const [kpiFilterDropdownValues, setKpiDropdownValues] = useState<any[]>([]);
    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});

    const promoLibraryAttributeTypes = tpoConfig?.promoLibraryAttributeTypes;

    const tableHeight = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    tableHeight.current = useFillHeightToFitScreen({
        id: promoLibraryTableId,
        height: tableHeight.current,
        buffer: 110, //footer
    });

    const { mutate: updatePromoLibrary, loading: updatePromoLibraryLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.updatePromoLibraryUrl,
        method: 'post',
    });

    //promo library data
    const {
        loading: promoLibraryTableDataLoading,
        error: promoLibraryTableDataError,
        data: promoLibraryTableData,
        count: promoLibraryTableDataCount,
    } = useFetchToolsLibraryData({
        method: 'post',
        queryConfig: {
            enabled: Boolean(
                promoLibraryTableVariable?.kpis?.length > 0 &&
                    Object.keys(promoLibraryTableVariable?.filters)?.length > 0 &&
                    uiDimensionConfigData?.filterKeys?.length > 0,
            ),
        },
        url: tradePromoOptimizationUrls.getPromoLibraryTableDataUrl,
        queryKey: ['tpo-promo-library-data', 'tpo-promo-library-table-data', promoLibraryTableVariable],
        variables: { ...(promoLibraryTableVariable || {}) },
        kpiConfigData: promoLibraryKpiConfigData,
        attributeTypes: promoLibraryAttributeTypes,
    });

    useEffect(() => {
        const valueList: any[] = [];
        const updatedList = Object.keys(promoLibraryKpiConfigData ?? {})?.map((configKey) => {
            valueList.push({ key: configKey, storage_type: promoLibraryKpiConfigData?.[configKey]['storageType'] });
            return {
                ...promoLibraryKpiConfigData?.[configKey],
                label: promoLibraryKpiConfigData?.[configKey]['label'],
                value: configKey,
                isSelected: true,
                pre: promoLibraryKpiConfigData?.[configKey]['pre'],
                post: promoLibraryKpiConfigData?.[configKey]['post'],
            };
        });
        setPromoLibraryTableVariable((prev: any) => {
            return {
                ...prev,
                offset: 0,
                kpis: valueList,
                sort_by: promoLibraryAttributeTypes?.[0]?.sortKey,
                sort_order: 'asc',
            };
        });
        setKpiDropdownValues(updatedList);
    }, [promoLibraryKpiConfigData]);

    useEffect(() => {
        const updatedFilters = transformFiltersV2(applyFilterObject, uiDimensionConfigData?.filterKeys ?? []);
        setPromoLibraryTableVariable((prev: any) => ({ ...prev, filters: updatedFilters, offset: 0 }));
    }, [applyFilterObject]);

    function infiniteScrollOnNext(loading: boolean) {
        if (loading) return;
        const newOffset = promoLibraryTableVariable.offset + promoLibraryTableVariable.limit;
        setPromoLibraryTableVariable({ ...promoLibraryTableVariable, offset: newOffset });
    }

    //GET  PROMO LIBRARY KPI CONFIG DATA FROM BACKEND
    const { loading: promoLibraryKPIConfigLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: {},
        queryKey: ['tpo-promo-library-kpi-config'],
        url: `${tradePromoOptimizationUrls.getPromoLibraryKpiUrl}/promo_library`,
        variables: {},
        parserFunction(data: any) {
            const res: PromoLibraryKpiConfigType = {};
            data?.forEach((item: any) => {
                res[item?.kpi_key] = {
                    pre: item?.pre,
                    post: item?.post,
                    label: item?.kpi_name,
                    allowNegative: item?.allow_negative,
                    isEditable: item?.is_editable,
                    maxValue: item?.max_value,
                    minValue: item?.min_value,
                    type: item?.kpi_type,
                    storageType: item?.storage_type,
                    allowedDecimals: item?.allowed_decimals,
                    allowedValues: item?.allowed_values,
                };
            });
            setPromoLibraryKpiConfigData(res);
            return {};
        },
    });

    function onEdit(
        from: 'cell' | 'column',
        kpiObject: PromoLibraryKpiConfigType,
        value?: any,
        rowId?: number,
        rowLabel?: string,
    ) {
        setOpenEditModal({
            kpiData: {
                kpiObject,
                value: from === 'column' ? undefined : value,
                rowId: from === 'column' ? undefined : rowId,
                rowLabel: from === 'column' ? undefined : rowLabel,
            },
            open: true,
        });
    }

    const columns = useMemo(() => {
        if (!promoLibraryTableData) return [];
        const tempColumns = kpiFilterDropdownValues
            ?.filter((item) => item.isSelected)
            ?.map((item) => {
                return {
                    label: item?.label,
                    key: item?.label,
                    kpiInfo: item,
                    sortKey: item?.storageType === 'range' ? false : item?.value,
                };
            });
        const memoisedColumns = getPromoLibraryTableColumns(promoLibraryAttributeTypes, tempColumns, onEdit, {
            sortBy: promoLibraryTableVariable?.sort_by,
            sortOrder: promoLibraryTableVariable?.sort_order === 'asc' ? 'ascend' : 'descend',
        });
        return memoisedColumns;
    }, [promoLibraryTableData]);

    const commonLoading = filterUIDimensionsLoading || promoLibraryTableDataLoading || promoLibraryKPIConfigLoading;

    const isErrorStatus = promoLibraryTableDataError;
    const footerFirstText = `${
        'Showing ' +
        Number(promoLibraryTableData?.length ?? 0)?.toLocaleString() +
        ' of ' +
        formatNumberWithCommas(promoLibraryTableDataCount, 0) +
        ' records'
    }`;
    const footerSecondText = '';
    const footerThirdText = '';
    const footerFourthText = '';

    const onFilterSaveClickHandler = (resultObj: any) => {
        setAllCheckedAttribute(resultObj.allCheckedAttribute);
        setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
        setApplyFilterObject(resultObj.applyFilterObject);
    };

    const commonVariables = useMemo(() => {
        if (selectedFilter?.value) {
            // const filters = transformFilterObjectWithDimensionsInfo(
            //     tempApplyFilterObject,
            //     uiDimensionConfigData.filterKeys,
            // );
            const updatedFilters = transformFiltersV2(
                tempApplyFilterObject,
                uiDimensionConfigData.filterKeys,
                selectedFilter.value,
            );

            // removePropertyByKeyName(filters, selectedFilter.value);
            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
                library_group: ToolTabKeys.PromoLibrary,
            };
        }
    }, [selectedFilter]);

    return (
        <div>
            <div className="flex justify-between mt-12 mb-2 items-center">
                <div className="flex items-center gap-2"></div>

                <div className="flex justify-end gap-2 items-center">
                    {/* <Legend className="legend-size-square" data={legendItems} /> */}
                    <NewGlobalFilter
                        availableFilters={availableFilters}
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetFilterObject}
                        onSubmitHandler={onFilterSaveClickHandler}
                        url={tradePromoOptimizationUrls.getAttributeNames}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={commonVariables}
                        filterDimensionInfo={uiDimensionConfigData?.filterDimensionInfo}
                        showAttributePanelToLeft
                    />
                    <KPISelector
                        buttonComponent={
                            <Button
                                onClick={() => {
                                    setIsKpiFilterVisible(!isKpiFilterVisible);
                                }}
                                icon={<ProjectOutlined style={{ position: 'relative', top: -3 }} />}
                                style={{ borderRadius: 4, margin: 0 }}
                                type="default"
                            >
                                KPI
                            </Button>
                        }
                        isVisible={isKpiFilterVisible}
                        setIsVisible={setIsKpiFilterVisible}
                        kpiList={kpiFilterDropdownValues}
                        onSubmit={(values) => {
                            const updatedList = kpiFilterDropdownValues?.map((item) => {
                                return {
                                    ...item,
                                    isSelected: values.includes(item?.value),
                                };
                            });
                            const updatedValues = kpiFilterDropdownValues
                                ?.filter((kpiItem) => {
                                    return values.includes(kpiItem?.value);
                                })
                                .map((item) => ({ key: item?.value, storage_type: item?.storageType }));
                            setPromoLibraryTableVariable({
                                ...promoLibraryTableVariable,
                                offset: 0,
                                kpis: updatedValues,
                                sort_by:
                                    updatedValues?.find((item) => item?.key === promoLibraryTableVariable?.sort_by)
                                        ?.key ?? promoLibraryAttributeTypes?.[0]?.sortKey,
                                sort_order: promoLibraryTableVariable?.sort_order ?? 'asc',
                            });
                            setKpiDropdownValues(updatedList);
                        }}
                        variableName={'KPI'}
                        hideSearch={true}
                    />
                </div>
            </div>
            {/*3.  Filter Ribbon  */}
            <div className="my-3">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetFilterObject}
                    onSubmitHandler={(obj: any) => {
                        setAllCheckedAttribute(obj.allCheckedAttribute);
                        setAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                        setApplyFilterObject(obj.applyFilterObject);
                    }}
                />
            </div>
            <InfiniteScroller
                data={promoLibraryTableData}
                loading={commonLoading}
                hasMore={promoLibraryTableData?.length < (promoLibraryTableDataCount || 0) || commonLoading}
                height={
                    promoLibraryTableData?.length && !promoLibraryTableDataError
                        ? tableHeight.current ?? undefined
                        : isNaN(Number(tableHeight.current))
                        ? undefined
                        : Number(tableHeight.current) - 1
                }
                //height={200}
                //     loader={true}
                onNext={() => infiniteScrollOnNext(commonLoading)}
            >
                <CustomTable
                    bordered
                    onChange={(pagination, filter, sort: any, extra) => {
                        if (sort?.order) {
                            const sortMapping: any = {
                                descend: 'desc',
                                ascend: 'asc',
                            };

                            setPromoLibraryTableVariable({
                                ...promoLibraryTableVariable,
                                sort_by: sort?.column?.sortKey,
                                sort_order: sortMapping?.[sort?.order] || 'asc',
                                offset: 0,
                            });
                        }
                    }}
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }} //adding x axis scrollbar on table
                    id={promoLibraryTableId}
                    columns={columns}
                    // loading={commonLoading}
                    scroll={{ x: '100%' }}
                    showSorterTooltip={false}
                    dataSource={promoLibraryTableData}
                    pagination={false}
                    style={{
                        opacity: commonLoading ? '0.3' : 1,
                        pointerEvents: commonLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: commonLoading ? (
                            <></>
                        ) : promoLibraryTableDataError ? (
                            <TableErrorBox error={promoLibraryTableDataError} height={tableHeight.current} />
                        ) : (
                            ''
                        ),
                    }}
                />
            </InfiniteScroller>
            <Footer
                type={isErrorStatus ? 'error' : 'default'}
                items={[footerFirstText, footerSecondText, footerThirdText, footerFourthText]}
            />
            <EditToolsMetricsModal
                updatePromoLibraryLoading={updatePromoLibraryLoading}
                data={openEditModal.kpiData}
                open={openEditModal.open}
                onSubmit={(payload: any) => {
                    const updatedPayload = {
                        ...(payload ?? {}),
                        library_group: ToolTabKeys.PromoLibrary,
                        filters: promoLibraryTableVariable.filters,
                    };
                    //first clear the variables so that on success fresh api can be triggered
                    updatePromoLibrary(updatedPayload, {
                        onSuccess: (data: any) => {
                            setPromoLibraryTableVariable({ ...promoLibraryTableVariable, offset: 0 });
                            sendNotification('success', tpoNotificationMessages.EDIT_PROMO_LIBRARY_SUCCESS);
                            //on success, REMOVE THE CACHE

                            queryClient.removeQueries({
                                exact: false,
                                queryKey: ['tpo-promo-library-data', 'tpo-promo-library-table-data'],
                            });
                            setOpenEditModal({ ...initialEditModalState });
                        },
                        onError: (error: any) => {
                            const errorMsg = generateErrorMsg(
                                String(error?.response?.data?.detail || ''),
                                ErrorMessages.GENERALIZED_ERROR_MSG,
                            );
                            //show error message
                            sendNotification('error', errorMsg);
                            setOpenEditModal({ ...initialEditModalState });
                        },
                    });
                }}
                onCancel={() => {
                    setOpenEditModal({ ...initialEditModalState });
                }}
            />
        </div>
    );
};

export default PromoLibraryContainer;
